var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { GFIDProvider } from "healthex-common/healthcareorganization.dtos";
import { isOk, voidResult } from "healthex-common/result";
import { usaStateFromName } from "healthex-common/usaStates";
import { UserPermissions, UserType } from "healthex-common/user.dtos";
import { getFullName, getJWTPayload } from "healthex-common/utils";
import { computed, observable, reaction } from "mobx";
import { updateHeapGlobalEventProperties } from "../Infra/LoadHeap";
const ANONYMOUS_USER_ID = "anonymousUserId";
const TOKEN_REFRESH_INTERVAL_MS = 60 * 1000;
const TOKEN_EXPIRY_BUFFER_SECONDS = 90;
function reportAnonymousUserIdError(message, error) {
    console.error(message, error);
    const attributes = {
        errorName: error instanceof Error ? error.name : "Unknown",
        errorMessage: error instanceof Error ? error.message : String(error),
        isEmbedded: top !== self,
        isSecureContext: typeof isSecureContext === "boolean" ? isSecureContext : null,
    };
    if (typeof (newrelic === null || newrelic === void 0 ? void 0 : newrelic.log) === "function") {
        newrelic.log(message, { level: "error", customAttributes: attributes });
    }
    if (typeof (newrelic === null || newrelic === void 0 ? void 0 : newrelic.noticeError) === "function") {
        const noticedError = error instanceof Error ? error : new Error(message);
        newrelic.noticeError(noticedError, { context: message, ...attributes });
    }
}
export function createAnonymousUserIdIfNeeded() {
    try {
        const anonymousId = localStorage.getItem(ANONYMOUS_USER_ID);
        if (!anonymousId) {
            const array = new BigUint64Array(1);
            crypto.getRandomValues(array);
            const value = array[0] % BigInt(Number.MAX_SAFE_INTEGER);
            localStorage.setItem(ANONYMOUS_USER_ID, value.toString());
        }
    }
    catch (e) {
        reportAnonymousUserIdError("Error creating anonymous user id", e);
    }
}
export function getAnonymousUserId() {
    try {
        return localStorage.getItem(ANONYMOUS_USER_ID);
    }
    catch {
        return null;
    }
}
export function generateOAuthState() {
    const array = new Uint32Array(4);
    window.crypto.getRandomValues(array);
    const state = Array.from(array)
        .map((b) => b.toString(16).padStart(8, "0"))
        .join("");
    return state;
}
export var LoginFlow;
(function (LoginFlow) {
    LoginFlow["PASSWORD"] = "PASSWORD";
    LoginFlow["SSO"] = "SSO";
    LoginFlow["SMART_CODE"] = "SMART_CODE";
    LoginFlow["SMART_CODE_REAUTH"] = "SMART_CODE_REAUTH";
    LoginFlow["GFID_CLEAR"] = "GFID_CLEAR";
    LoginFlow["GFID_CLEAR_REAUTH"] = "GFID_CLEAR_REAUTH";
    LoginFlow["GFID_CLEAR_IAL2"] = "GFID_CLEAR_IAL2";
    LoginFlow["GFID_GOOGLE"] = "GFID_GOOGLE";
    LoginFlow["GFID_MICROSOFT"] = "GFID_MICROSOFT";
    LoginFlow["GFID_APPLE"] = "GFID_APPLE";
    LoginFlow["GFID_HOSTED_SSO"] = "GFID_HOSTED_SSO";
    LoginFlow["BOT_LOGIN"] = "BOT_LOGIN";
})(LoginFlow || (LoginFlow = {}));
export class AuthManager {
    get token() {
        var _a, _b;
        return (_b = (_a = this.tokenInfo) === null || _a === void 0 ? void 0 : _a.token) !== null && _b !== void 0 ? _b : null;
    }
    get isLoggedIn() {
        return !!this.tokenInfo;
    }
    get tokenPayload() {
        if (!this.tokenInfo)
            return null;
        return getJWTPayload(this.tokenInfo.token);
    }
    get permissions() {
        var _a, _b;
        return (_b = (_a = this.tokenPayload) === null || _a === void 0 ? void 0 : _a.permissions) !== null && _b !== void 0 ? _b : [];
    }
    get isProvider() {
        return this.permissions.includes(UserPermissions.PROVIDER);
    }
    get isEmbeddedProvider() {
        return this.isProvider && !!this.associatedPatientMRN;
    }
    get userType() {
        var _a, _b;
        return (_b = (_a = this.tokenPayload) === null || _a === void 0 ? void 0 : _a.type) !== null && _b !== void 0 ? _b : null;
    }
    get id() {
        var _a, _b;
        return (_b = (_a = this.tokenPayload) === null || _a === void 0 ? void 0 : _a.sub) !== null && _b !== void 0 ? _b : null;
    }
    get restrictedToOrgId() {
        var _a, _b;
        return (_b = (_a = this.tokenPayload) === null || _a === void 0 ? void 0 : _a.restrictedToOrgId) !== null && _b !== void 0 ? _b : null;
    }
    get isHostedSsoRestricted() {
        return !!this.restrictedToOrgId;
    }
    get patientEmail() {
        var _a, _b;
        if (((_a = this.tokenInfo) === null || _a === void 0 ? void 0 : _a.type) !== UserType.PATIENT) {
            return null;
        }
        return (_b = this.tokenInfo.email) !== null && _b !== void 0 ? _b : null;
    }
    get mrn() {
        var _a, _b;
        if (((_a = this.tokenInfo) === null || _a === void 0 ? void 0 : _a.type) !== UserType.PATIENT) {
            return null;
        }
        return (_b = this.tokenInfo.mrn) !== null && _b !== void 0 ? _b : null;
    }
    setPatientConsentOrgId(orgId) {
        var _a;
        if (((_a = this.tokenInfo) === null || _a === void 0 ? void 0 : _a.type) === UserType.PATIENT) {
            this._patientConsentOrgId = orgId;
        }
    }
    get patientConsentOrgId() {
        return this._patientConsentOrgId;
    }
    get orgId() {
        var _a, _b, _c;
        if (((_a = this.tokenInfo) === null || _a === void 0 ? void 0 : _a.type) === UserType.PATIENT) {
            return null;
        }
        return (_c = (_b = this.tokenPayload) === null || _b === void 0 ? void 0 : _b.organizationId) !== null && _c !== void 0 ? _c : null;
    }
    get anyOrgId() {
        var _a;
        return (_a = this.patientConsentOrgId) !== null && _a !== void 0 ? _a : this.orgId;
    }
    get name() {
        if (!this.tokenInfo)
            return null;
        return getFullName(this.tokenInfo);
    }
    get firstName() {
        var _a;
        return (_a = this.tokenInfo) === null || _a === void 0 ? void 0 : _a.firstName;
    }
    get lastName() {
        var _a;
        return (_a = this.tokenInfo) === null || _a === void 0 ? void 0 : _a.lastName;
    }
    get providerId() {
        var _a;
        return (_a = this.tokenInfo) === null || _a === void 0 ? void 0 : _a.providerId;
    }
    get state() {
        var _a, _b;
        if (((_a = this.tokenInfo) === null || _a === void 0 ? void 0 : _a.type) === "PATIENT" || ((_b = this.tokenInfo) === null || _b === void 0 ? void 0 : _b.type) === "PATIENT_READ_ONLY") {
            return usaStateFromName(this.tokenInfo.state);
        }
        return null;
    }
    get createdAt() {
        var _a, _b;
        if (((_a = this.tokenInfo) === null || _a === void 0 ? void 0 : _a.type) === "PATIENT" || ((_b = this.tokenInfo) === null || _b === void 0 ? void 0 : _b.type) === "PATIENT_READ_ONLY") {
            return this.tokenInfo.createdAt;
        }
        return null;
    }
    get email() {
        var _a, _b, _c;
        if (((_a = this.tokenInfo) === null || _a === void 0 ? void 0 : _a.type) === "SCREENSHOT_USER") {
            return null;
        }
        return (_c = (_b = this.tokenInfo) === null || _b === void 0 ? void 0 : _b.email) !== null && _c !== void 0 ? _c : null;
    }
    get phone() {
        var _a, _b, _c;
        if (((_a = this.tokenInfo) === null || _a === void 0 ? void 0 : _a.type) === "SCREENSHOT_USER") {
            return null;
        }
        return (_c = (_b = this.tokenInfo) === null || _b === void 0 ? void 0 : _b.phone) !== null && _c !== void 0 ? _c : null;
    }
    get phoneVerified() {
        var _a, _b, _c;
        if (((_a = this.tokenInfo) === null || _a === void 0 ? void 0 : _a.type) !== "PATIENT" && ((_b = this.tokenInfo) === null || _b === void 0 ? void 0 : _b.type) !== "PATIENT_READ_ONLY") {
            return false;
        }
        if (!this.phone) {
            return false;
        }
        return (_c = this.tokenInfo.phoneVerified) !== null && _c !== void 0 ? _c : false;
    }
    get formattedPhone() {
        if (!this.phone)
            return null;
        const match = this.phone.match(/^\+1(\d{3})(\d{3})(\d{4})$/);
        if (match) {
            return `${match[1]}-${match[2]}-${match[3]}`;
        }
        return this.phone;
    }
    get title() {
        var _a, _b, _c, _d, _e;
        if (((_a = this.tokenInfo) === null || _a === void 0 ? void 0 : _a.type) === "SCREENSHOT_USER" ||
            ((_b = this.tokenInfo) === null || _b === void 0 ? void 0 : _b.type) === "PATIENT" ||
            ((_c = this.tokenInfo) === null || _c === void 0 ? void 0 : _c.type) === "PATIENT_READ_ONLY") {
            return null;
        }
        return (_e = (_d = this.tokenInfo) === null || _d === void 0 ? void 0 : _d.title) !== null && _e !== void 0 ? _e : null;
    }
    get age() {
        var _a, _b, _c;
        if (((_a = this.tokenInfo) === null || _a === void 0 ? void 0 : _a.type) !== "PATIENT" && ((_b = this.tokenInfo) === null || _b === void 0 ? void 0 : _b.type) !== "PATIENT_READ_ONLY") {
            return null;
        }
        return (_c = this.tokenInfo.age) !== null && _c !== void 0 ? _c : null;
    }
    get dateOfBirth() {
        var _a, _b, _c, _d;
        if (((_a = this.tokenInfo) === null || _a === void 0 ? void 0 : _a.type) !== "PATIENT" && ((_b = this.tokenInfo) === null || _b === void 0 ? void 0 : _b.type) !== "PATIENT_READ_ONLY") {
            return null;
        }
        return (_d = (_c = this.tokenInfo) === null || _c === void 0 ? void 0 : _c.dateOfBirth) !== null && _d !== void 0 ? _d : null;
    }
    get isUnderage() {
        if (this.state === null || this.age === null) {
            return false;
        }
        return this.stateLawsService.isUnderage(this.state, this.age);
    }
    get avatarUrl() {
        var _a, _b, _c;
        if (((_a = this.tokenInfo) === null || _a === void 0 ? void 0 : _a.type) === "SCREENSHOT_USER") {
            return null;
        }
        return (_c = (_b = this.tokenInfo) === null || _b === void 0 ? void 0 : _b.avatarUrl) !== null && _c !== void 0 ? _c : null;
    }
    get orgLogoUrl() {
        var _a, _b, _c, _d, _e;
        if (((_a = this.tokenInfo) === null || _a === void 0 ? void 0 : _a.type) === "PATIENT" ||
            ((_b = this.tokenInfo) === null || _b === void 0 ? void 0 : _b.type) === "SCREENSHOT_USER" ||
            ((_c = this.tokenInfo) === null || _c === void 0 ? void 0 : _c.type) === "PATIENT_READ_ONLY") {
            return null;
        }
        return (_e = (_d = this.tokenInfo) === null || _d === void 0 ? void 0 : _d.organizationLogoUrl) !== null && _e !== void 0 ? _e : null;
    }
    get orgName() {
        var _a, _b, _c, _d, _e;
        if (((_a = this.tokenInfo) === null || _a === void 0 ? void 0 : _a.type) === "PATIENT" ||
            ((_b = this.tokenInfo) === null || _b === void 0 ? void 0 : _b.type) === "SCREENSHOT_USER" ||
            ((_c = this.tokenInfo) === null || _c === void 0 ? void 0 : _c.type) === "PATIENT_READ_ONLY") {
            return null;
        }
        return (_e = (_d = this.tokenInfo) === null || _d === void 0 ? void 0 : _d.organizationName) !== null && _e !== void 0 ? _e : null;
    }
    get orgState() {
        var _a, _b, _c, _d;
        if (((_a = this.tokenInfo) === null || _a === void 0 ? void 0 : _a.type) === "PATIENT" ||
            ((_b = this.tokenInfo) === null || _b === void 0 ? void 0 : _b.type) === "SCREENSHOT_USER" ||
            ((_c = this.tokenInfo) === null || _c === void 0 ? void 0 : _c.type) === "PATIENT_READ_ONLY") {
            return null;
        }
        if (!((_d = this.tokenInfo) === null || _d === void 0 ? void 0 : _d.organizationState)) {
            return null;
        }
        return usaStateFromName(this.tokenInfo.organizationState);
    }
    get isUserProfileEmpty() {
        return (this.firstName === null || this.lastName === null || (this.permissions.includes(UserPermissions.PROVIDER) && !this.providerId));
    }
    get isDataUser() {
        return this.userType === UserType.EXTERNAL_RESEARCHER;
    }
    get userForAvatar() {
        if (!this.id || !this.firstName || !this.lastName)
            return null;
        return {
            id: this.id,
            firstName: this.firstName,
            lastName: this.lastName,
            avatarUrl: this.avatarUrl,
        };
    }
    get passwordUpdatedAt() {
        if (!this.tokenInfo)
            return null;
        if (!("passwordUpdatedAt" in this.tokenInfo))
            return null;
        return this.tokenInfo.passwordUpdatedAt;
    }
    get onboardingComplete() {
        var _a, _b;
        return (_b = (_a = this.tokenInfo) === null || _a === void 0 ? void 0 : _a.onboardingComplete) !== null && _b !== void 0 ? _b : null;
    }
    get organizationIsLive() {
        var _a, _b;
        if (((_a = this.tokenInfo) === null || _a === void 0 ? void 0 : _a.type) === UserType.HEALTHCARE_ORGANIZATION || ((_b = this.tokenInfo) === null || _b === void 0 ? void 0 : _b.type) === UserType.EXTERNAL_RESEARCHER) {
            return this.tokenInfo.organizationIsLive;
        }
        return null;
    }
    get isOrgAdmin() {
        return this.permissions.includes(UserPermissions.ORG_ADMIN);
    }
    get isResearcher() {
        return this.permissions.includes(UserPermissions.RESEARCHER);
    }
    get gfidProvider() {
        var _a, _b;
        if (((_a = this.tokenInfo) === null || _a === void 0 ? void 0 : _a.type) !== "PATIENT" && ((_b = this.tokenInfo) === null || _b === void 0 ? void 0 : _b.type) !== "PATIENT_READ_ONLY") {
            return undefined;
        }
        return this.tokenInfo.gfidProvider;
    }
    get proxyAccess() {
        var _a, _b;
        if (((_a = this.tokenInfo) === null || _a === void 0 ? void 0 : _a.type) !== "PATIENT" && ((_b = this.tokenInfo) === null || _b === void 0 ? void 0 : _b.type) !== "PATIENT_READ_ONLY") {
            return undefined;
        }
        return this.tokenInfo.proxyAccess;
    }
    get blockedFromUsingIAL2() {
        var _a, _b;
        if (((_a = this.tokenInfo) === null || _a === void 0 ? void 0 : _a.type) !== "PATIENT" && ((_b = this.tokenInfo) === null || _b === void 0 ? void 0 : _b.type) !== "PATIENT_READ_ONLY") {
            return false;
        }
        return this.tokenInfo.gfidProvider === GFIDProvider.CLEAR && this.tokenInfo.authenticatedWithIAL2 === false;
    }
    get isIAL2Authorized() {
        var _a, _b;
        if (((_a = this.tokenInfo) === null || _a === void 0 ? void 0 : _a.type) !== "PATIENT" && ((_b = this.tokenInfo) === null || _b === void 0 ? void 0 : _b.type) !== "PATIENT_READ_ONLY") {
            return false;
        }
        return this.tokenInfo.authenticatedWithIAL2 === true;
    }
    get isSandbox() {
        var _a, _b, _c;
        if (((_a = this.tokenInfo) === null || _a === void 0 ? void 0 : _a.type) !== "PATIENT" && ((_b = this.tokenInfo) === null || _b === void 0 ? void 0 : _b.type) !== "PATIENT_READ_ONLY") {
            return false;
        }
        return (_c = this.tokenInfo.isSandbox) !== null && _c !== void 0 ? _c : false;
    }
    get associatedPatientMRN() {
        var _a;
        if (((_a = this.tokenInfo) === null || _a === void 0 ? void 0 : _a.type) !== "HEALTHCARE_ORGANIZATION") {
            return undefined;
        }
        return this.tokenInfo.associatedPatientMRN;
    }
    get consentingPatientInfo() {
        if (this.userType === UserType.PATIENT || this.userType === UserType.PATIENT_READ_ONLY) {
            if (this.id === null)
                return null;
            const { id, name, isUnderage, state, mrn, firstName, lastName, email, phone, dateOfBirth, phoneVerified } = this;
            return {
                id,
                name,
                isUnderage,
                state,
                mrn,
                firstName: firstName !== null && firstName !== void 0 ? firstName : null,
                lastName: lastName !== null && lastName !== void 0 ? lastName : null,
                email,
                phone,
                dateOfBirth: dateOfBirth !== null && dateOfBirth !== void 0 ? dateOfBirth : undefined,
                phoneVerified: phoneVerified !== null && phoneVerified !== void 0 ? phoneVerified : undefined,
            };
        }
        else {
            return null;
        }
    }
    async updateOrganizationIsLive(isLive) {
        var _a;
        if (((_a = this.tokenInfo) === null || _a === void 0 ? void 0 : _a.type) === UserType.HEALTHCARE_ORGANIZATION) {
            if (this.tokenInfo.organizationIsLive === isLive) {
                return;
            }
            this.tokenInfo.organizationIsLive = isLive;
            await this.setTokenInfo(this.tokenInfo);
        }
    }
    constructor(persistence, authService, stateLawsService, embeddedManager, environment) {
        this.persistence = persistence;
        this.authService = authService;
        this.stateLawsService = stateLawsService;
        this.embeddedManager = embeddedManager;
        this.environment = environment;
        this.invalidEmbed = false;
        this._patientConsentOrgId = null;
        this.isFirstTimeLogin = false;
        this.linkingStatus = null;
        this.lastUserActivity = Date.now();
        this.refreshPromise = null;
        this.logoutPromise = null;
        this.logoutVoluntary = false;
        this.onVisibilityChangeForTokenRefresh = () => {
            if (document.visibilityState === "visible") {
                void this.checkAccessTokenValidityAndRefresh();
            }
        };
        this.accountLinkingConflict = null;
        this.tokenInfo = !this.embeddedManager.embedded ? this.persistence.getTokenInfo() : null;
        this.accountLinkingConflict = this.persistence.getConflictResolutionData();
        this.invalidateTokenInfoIfNeeded();
        reaction(() => this.isLoggedIn, () => {
            if (this.isLoggedIn) {
                void this.refreshTokenInfo();
            }
        }, { fireImmediately: true });
        this.setupActivityTracking();
        document.addEventListener("visibilitychange", this.onVisibilityChangeForTokenRefresh);
        this.setupTokenRefresh();
    }
    setupActivityTracking() {
        const trackActivity = () => {
            this.lastUserActivity = Date.now();
        };
        ["mousedown", "keydown", "touchstart", "scroll"].forEach((event) => {
            document.addEventListener(event, trackActivity, { passive: true });
        });
    }
    async prepareAuthenticatedRequest() {
        var _a, _b;
        if (this.logoutPromise)
            return;
        const expiresInSeconds = ((_b = (_a = this.tokenInfo) === null || _a === void 0 ? void 0 : _a.expiration) !== null && _b !== void 0 ? _b : 0) - Date.now() / 1000;
        if (expiresInSeconds <= TOKEN_EXPIRY_BUFFER_SECONDS) {
            await this.refreshAccessToken();
        }
    }
    async waitForPendingRefresh() {
        if (this.refreshPromise) {
            await this.refreshPromise.catch(() => { });
        }
    }
    recordApiActivity() {
        const isPatient = this.userType === UserType.PATIENT || this.userType === UserType.PATIENT_READ_ONLY;
        if (isPatient) {
            this.lastUserActivity = Date.now();
        }
    }
    setupTokenRefresh() {
        if (this.refreshInterval) {
            clearInterval(this.refreshInterval);
            this.refreshInterval = undefined;
        }
        void this.checkAccessTokenValidityAndRefresh();
        this.refreshInterval = setInterval(() => {
            void this.checkAccessTokenValidityAndRefresh();
        }, TOKEN_REFRESH_INTERVAL_MS);
    }
    async checkAccessTokenValidityAndRefresh() {
        var _a, _b;
        if (!this.isLoggedIn || this.logoutPromise)
            return;
        const expiresInSeconds = ((_b = (_a = this.tokenInfo) === null || _a === void 0 ? void 0 : _a.expiration) !== null && _b !== void 0 ? _b : 0) - Date.now() / 1000;
        console.log(`TokenValidityTimer Token expires in ${Math.round(expiresInSeconds)}s, buffer=${TOKEN_EXPIRY_BUFFER_SECONDS}s`);
        if (expiresInSeconds <= TOKEN_EXPIRY_BUFFER_SECONDS) {
            console.log("TokenValidityTimer Refreshing token");
            await this.refreshAccessToken();
        }
    }
    async refreshAccessToken() {
        if (this.logoutPromise) {
            return false;
        }
        if (this.refreshPromise) {
            return this.refreshPromise;
        }
        this.refreshPromise = this.doRefreshAccessToken();
        const result = await this.refreshPromise;
        this.refreshPromise = null;
        return result;
    }
    async doRefreshAccessToken() {
        try {
            const isPatient = this.userType === UserType.PATIENT || this.userType === UserType.PATIENT_READ_ONLY;
            const refreshUserType = isPatient ? "PATIENT" : "ORG_USER";
            const result = await this.authService.refreshAccessToken(refreshUserType, this.id, this.lastUserActivity);
            if (result) {
                console.log("Token refresh success, new token received");
                await this.handleTokenResult(result, null);
                return true;
            }
            else {
                console.log("Token refresh rejected, logging out");
                this.refreshPromise = null;
                await this.logout(false);
                return false;
            }
        }
        catch (e) {
            console.error("Token refresh error, will keep trying:", e);
            return false;
        }
    }
    invalidateTokenInfoIfNeeded() {
        var _a;
        const tokenExpirationCutoffTimestamp = this.environment.TOKEN_EXPIRATION_CUTOFF_TIMESTAMP_SECONDS;
        if (!tokenExpirationCutoffTimestamp || tokenExpirationCutoffTimestamp.length === 0)
            return;
        if (!this.isLoggedIn || ((_a = this.tokenPayload) === null || _a === void 0 ? void 0 : _a.type) !== UserType.PATIENT)
            return;
        const tokenIssuedAt = this.tokenPayload.iat;
        if (tokenIssuedAt < parseInt(tokenExpirationCutoffTimestamp, 10)) {
            void this.logout(false);
        }
    }
    async authenticateByPassword(email, password, organizationId) {
        const generateTokenResult = await this.authService.generateTokenByPassword(email, password, organizationId);
        return await this.handleTokenResult(generateTokenResult, LoginFlow.PASSWORD);
    }
    async getOrganizationsForEmail(email, redirectURL) {
        return await this.authService.getOrganizationsForEmail(email, redirectURL);
    }
    async getAuthTypeByEmail(email, redirectTo) {
        return await this.authService.getAuthType({ email }, redirectTo);
    }
    async getAuthTypeByOrgId(organizationId, redirectTo) {
        return await this.authService.getAuthType({ organizationId }, redirectTo);
    }
    async authenticateByClearIAL2(authCode, redirectUri, organizationId, authTarget, sandboxMode, heapGlobalEventProperties, linkingToken) {
        var _a;
        const existingAuthToken = this.isLoggedIn && ((_a = this.tokenPayload) === null || _a === void 0 ? void 0 : _a.type) === UserType.PATIENT && this.token ? this.token : undefined;
        const generateTokenResult = await this.authService.generateTokenByClearGFIDWithIAL2(authCode, redirectUri, organizationId, authTarget, sandboxMode, heapGlobalEventProperties, linkingToken, existingAuthToken);
        return await this.handleTokenResult(generateTokenResult, LoginFlow.GFID_CLEAR_IAL2);
    }
    async invalidateIAL2Token() {
        if (this.tokenInfo && (this.tokenInfo.type === UserType.PATIENT || this.tokenInfo.type === UserType.PATIENT_READ_ONLY)) {
            await this.setTokenInfo({
                ...this.tokenInfo,
            });
        }
    }
    async authenticateByPatientId(patientId, organizationName) {
        const generateTokenResult = await this.authService.generateTokenByPatientId(this, patientId, organizationName);
        return await this.handleTokenResult(generateTokenResult, LoginFlow.BOT_LOGIN);
    }
    async authenticateByPatientIdWithIAL2ForTesting(authenticationProvider, patientId, organizationId) {
        const generateTokenResult = await this.authService.generateTokenByPatientIdWithIAL2ForTesting(authenticationProvider, patientId, organizationId);
        return await this.handleTokenResult(generateTokenResult, LoginFlow.GFID_CLEAR_IAL2);
    }
    async handleSAMLLogin(token) {
        return await this.handleExistingTokenLogin(token, LoginFlow.SSO);
    }
    async handleSocialLogin(token, loginFlow, isFirstTimeLogin) {
        if (isFirstTimeLogin) {
            this.isFirstTimeLogin = true;
        }
        return await this.handleExistingTokenLogin(token, loginFlow);
    }
    async handleExistingTokenLogin(token, loginFlow) {
        const generateTokenResult = await this.authService.generateTokenByExistingToken(token);
        return await this.handleTokenResult(generateTokenResult, loginFlow);
    }
    async authenticateByToken(token) {
        return await this.authService.getAuthenticatedUserWithDetails({ token });
    }
    async authenticateByShortLivedToken(token) {
        const generateTokenResult = await this.authService.generateTokenFromShortLivedToken(token);
        await this.handleTokenResult(generateTokenResult, null);
    }
    async resetPassword(newPassword, token) {
        await this.authService.resetPassword(newPassword, token);
    }
    async sendPasswordResetUrl(email, organizationId) {
        await this.authService.sendPasswordResetEmail(email, organizationId);
    }
    async changePassword(input) {
        return await this.authService.changePassword(input);
    }
    async authenticateScreenshotUser(generateTokenResult) {
        if (generateTokenResult.user.type !== UserType.SCREENSHOT_USER) {
            throw new Error("This API should only be used for screenshot users");
        }
        await this.handleTokenResult(generateTokenResult, LoginFlow.BOT_LOGIN);
    }
    async applyDeviceHintOtpLogin(result) {
        await this.handleTokenResult(result, LoginFlow.GFID_CLEAR_REAUTH);
    }
    async handleTokenResult(generateTokenResult, loginFlow) {
        if (this.logoutPromise) {
            return isOk(generateTokenResult) ? voidResult() : generateTokenResult;
        }
        if (isOk(generateTokenResult)) {
            const { token, expiration, user, isFirstTimeLogin, linkingStatus, accountLinkingConflict } = generateTokenResult;
            if (accountLinkingConflict) {
                this.accountLinkingConflict = accountLinkingConflict;
                this.persistence.setConflictResolutionData(accountLinkingConflict);
            }
            if (isFirstTimeLogin) {
                this.isFirstTimeLogin = true;
            }
            if (linkingStatus) {
                this.linkingStatus = linkingStatus;
            }
            await this.updateTokenInfoFromTokenAndUser(token, expiration, user, loginFlow);
            this.setupTokenRefresh();
            return voidResult();
        }
        else {
            return generateTokenResult;
        }
    }
    async updateTokenInfoFromTokenAndUser(token, expiration, user, loginFlow) {
        var _a, _b;
        const { firstName, lastName } = user;
        let providerId = null;
        if ("providerId" in user) {
            providerId = user.providerId;
        }
        if (user.type === UserType.PATIENT || user.type === UserType.PATIENT_READ_ONLY) {
            let { phone: patientPhone, email: patientEmail, age: patientAge, proxyAccess, authenticatedWithIAL2, mrn, isSandbox, dateOfBirth, phoneVerified, avatarUrl: patientAvatarUrl, } = user;
            if (this.tokenInfo && (this.tokenInfo.type === "PATIENT" || this.tokenInfo.type === "PATIENT_READ_ONLY")) {
                patientPhone = patientPhone !== null && patientPhone !== void 0 ? patientPhone : this.tokenInfo.phone;
                patientEmail = patientEmail !== null && patientEmail !== void 0 ? patientEmail : this.tokenInfo.email;
                patientAge = patientAge !== null && patientAge !== void 0 ? patientAge : this.tokenInfo.age;
                proxyAccess = proxyAccess !== null && proxyAccess !== void 0 ? proxyAccess : this.tokenInfo.proxyAccess;
                authenticatedWithIAL2 = authenticatedWithIAL2 !== null && authenticatedWithIAL2 !== void 0 ? authenticatedWithIAL2 : this.tokenInfo.authenticatedWithIAL2;
                isSandbox = isSandbox !== null && isSandbox !== void 0 ? isSandbox : this.tokenInfo.isSandbox;
                dateOfBirth = dateOfBirth !== null && dateOfBirth !== void 0 ? dateOfBirth : this.tokenInfo.dateOfBirth;
                phoneVerified = phoneVerified !== null && phoneVerified !== void 0 ? phoneVerified : this.tokenInfo.phoneVerified;
                patientAvatarUrl = patientAvatarUrl !== null && patientAvatarUrl !== void 0 ? patientAvatarUrl : this.tokenInfo.avatarUrl;
            }
            const tokenInfo = {
                type: user.type,
                token,
                expiration,
                firstName,
                lastName,
                phone: patientPhone,
                email: patientEmail,
                state: user.state,
                age: patientAge,
                onboardingComplete: null,
                createdAt: user.createdAt,
                gfidProvider: user.gfidProvider,
                proxyAccess,
                providerId: null,
                authenticatedWithIAL2,
                mrn,
                isSandbox,
                dateOfBirth,
                phoneVerified,
                avatarUrl: patientAvatarUrl,
            };
            await this.setIdentifierForAnalytics(user, loginFlow);
            await this.setTokenInfo(tokenInfo);
        }
        else if (user.type === "SCREENSHOT_USER") {
            const tokenInfo = {
                type: user.type,
                token,
                expiration,
                firstName,
                lastName,
                onboardingComplete: null,
                providerId: null,
            };
            await this.setIdentifierForAnalytics(user, loginFlow);
            await this.setTokenInfo(tokenInfo);
        }
        else {
            const { name: organizationName, logoUrl: organizationLogoUrl } = user.organization;
            const { email, avatarUrl, phone, title, passwordUpdatedAt } = user;
            const organizationState = (_a = user.organization.state) !== null && _a !== void 0 ? _a : null;
            const onboardingComplete = user.onboardingComplete;
            const tokenInfo = {
                type: user.type,
                token,
                expiration,
                firstName,
                lastName,
                email,
                avatarUrl,
                providerId,
                organizationName,
                organizationLogoUrl,
                organizationState,
                onboardingComplete,
                phone,
                title,
                passwordUpdatedAt,
                organizationIsLive: user.organization.isLive,
                ...(user.type === UserType.HEALTHCARE_ORGANIZATION ? { associatedPatientMRN: (_b = user.associatedPatient) === null || _b === void 0 ? void 0 : _b.mrn } : {}),
            };
            await this.setIdentifierForAnalytics(user, loginFlow);
            await this.setTokenInfo(tokenInfo);
        }
    }
    async refreshTokenInfo() {
        if (!this.tokenInfo)
            return;
        const user = await this.authService.getAuthenticatedUserWithDetails(this);
        if (user.type === UserType.HEALTHCARE_ORGANIZATION && this.associatedPatientMRN) {
            user.associatedPatient = { mrn: this.associatedPatientMRN };
        }
        await this.updateTokenInfoFromTokenAndUser(this.tokenInfo.token, this.tokenInfo.expiration, user, null);
    }
    async regenerateToken() {
        if (!this.token)
            return;
        const generateTokenResult = await this.authService.generateTokenByExistingToken(this.token);
        await this.handleTokenResult(generateTokenResult, null);
    }
    async handleConflictResolutionResult(token, expiration, user) {
        if (this.logoutPromise)
            return;
        await this.updateTokenInfoFromTokenAndUser(token, expiration, user, null);
        this.setupTokenRefresh();
    }
    async logout(voluntary = true) {
        this.logoutVoluntary = this.logoutVoluntary || voluntary;
        if (this.logoutPromise) {
            return this.logoutPromise;
        }
        this.logoutPromise = this.doLogout();
        return this.logoutPromise;
    }
    async doLogout() {
        try {
            if (this.refreshInterval) {
                clearInterval(this.refreshInterval);
                this.refreshInterval = undefined;
            }
            await this.waitForPendingRefresh();
            const voluntaryForServer = this.logoutVoluntary;
            const isPatient = this.userType === UserType.PATIENT || this.userType === UserType.PATIENT_READ_ONLY;
            const userTypeForLogout = isPatient ? "PATIENT" : "ORG_USER";
            const warnServerLogoutFailure = (e) => {
                console.warn("Server logout failed; session cookies may not be cleared on the server", e);
            };
            await this.authService.serverLogout(userTypeForLogout, voluntaryForServer).catch(warnServerLogoutFailure);
            if (!voluntaryForServer && this.logoutVoluntary) {
                console.log("Voluntary logout upgraded while server logout was in-flight; sending follow-up /auth/logout with voluntary=true to clear hx_device");
                await this.authService.serverLogout(userTypeForLogout, true).catch(warnServerLogoutFailure);
            }
            this.isFirstTimeLogin = false;
            this.linkingStatus = null;
            this.clearAccountLinkingConflict();
            void this.setTokenInfo(null);
        }
        finally {
            this.logoutPromise = null;
            this.logoutVoluntary = false;
        }
    }
    clearAccountLinkingConflict() {
        this.accountLinkingConflict = null;
        this.persistence.setConflictResolutionData(null);
    }
    async getShortLivedTokenForAppLaunch() {
        if (!this.isProvider)
            throw new Error("Wrong user type");
        if (!this.token)
            throw new Error("No token");
        return await this.authService.getShortLivedTokenForAppLaunch(this, this.token);
    }
    async setIdentifierForAnalytics(user, loginFlow) {
        var _a;
        try {
            if (user.type === UserType.PATIENT) {
                heap.identify(user.id);
            }
            if (loginFlow) {
                heap.addUserProperties({ LoginFlow: loginFlow.toString(), userType: user.type.toString() });
                if (typeof (newrelic === null || newrelic === void 0 ? void 0 : newrelic.setUserId) === "function")
                    newrelic.setUserId(user.id);
            }
            if (this.anyOrgId) {
                heap.addEventProperties({ organizationId: this.anyOrgId });
            }
            updateHeapGlobalEventProperties({ organizationId: (_a = this.anyOrgId) !== null && _a !== void 0 ? _a : null });
        }
        catch (e) {
            console.log("heap exception", e);
        }
    }
    async setTokenInfo(tokenInfo) {
        if (tokenInfo !== null && this.logoutPromise) {
            return;
        }
        if (tokenInfo !== null && top !== self && !this.embeddedManager.isSmartLaunch && !this.embeddedManager.embedded) {
            console.error("Invalid user embed");
            this.invalidEmbed = true;
            return;
        }
        if (!this.embeddedManager.embedded) {
            this.persistence.setTokenInfo(tokenInfo);
        }
        this.tokenInfo = tokenInfo;
        if (tokenInfo === null) {
            try {
                heap.removeEventProperty("organizationId");
            }
            catch (e) {
                console.log("heap exception", e);
            }
        }
    }
    async getPatientTEFCAAuthInfo(patientId) {
        return await this.authService.getPatientTEFCAAuthInfo(patientId, this);
    }
}
__decorate([
    observable
], AuthManager.prototype, "tokenInfo", void 0);
__decorate([
    observable
], AuthManager.prototype, "invalidEmbed", void 0);
__decorate([
    observable
], AuthManager.prototype, "_patientConsentOrgId", void 0);
__decorate([
    observable
], AuthManager.prototype, "isFirstTimeLogin", void 0);
__decorate([
    observable
], AuthManager.prototype, "linkingStatus", void 0);
__decorate([
    computed
], AuthManager.prototype, "token", null);
__decorate([
    computed
], AuthManager.prototype, "isLoggedIn", null);
__decorate([
    computed
], AuthManager.prototype, "tokenPayload", null);
__decorate([
    computed
], AuthManager.prototype, "permissions", null);
__decorate([
    computed
], AuthManager.prototype, "isProvider", null);
__decorate([
    computed
], AuthManager.prototype, "isEmbeddedProvider", null);
__decorate([
    computed
], AuthManager.prototype, "userType", null);
__decorate([
    computed
], AuthManager.prototype, "id", null);
__decorate([
    computed
], AuthManager.prototype, "restrictedToOrgId", null);
__decorate([
    computed
], AuthManager.prototype, "isHostedSsoRestricted", null);
__decorate([
    computed
], AuthManager.prototype, "patientEmail", null);
__decorate([
    computed
], AuthManager.prototype, "mrn", null);
__decorate([
    computed
], AuthManager.prototype, "patientConsentOrgId", null);
__decorate([
    computed
], AuthManager.prototype, "orgId", null);
__decorate([
    computed
], AuthManager.prototype, "anyOrgId", null);
__decorate([
    computed
], AuthManager.prototype, "name", null);
__decorate([
    computed
], AuthManager.prototype, "firstName", null);
__decorate([
    computed
], AuthManager.prototype, "lastName", null);
__decorate([
    computed
], AuthManager.prototype, "providerId", null);
__decorate([
    computed
], AuthManager.prototype, "state", null);
__decorate([
    computed
], AuthManager.prototype, "createdAt", null);
__decorate([
    computed
], AuthManager.prototype, "email", null);
__decorate([
    computed
], AuthManager.prototype, "phone", null);
__decorate([
    computed
], AuthManager.prototype, "phoneVerified", null);
__decorate([
    computed
], AuthManager.prototype, "formattedPhone", null);
__decorate([
    computed
], AuthManager.prototype, "title", null);
__decorate([
    computed
], AuthManager.prototype, "age", null);
__decorate([
    computed
], AuthManager.prototype, "dateOfBirth", null);
__decorate([
    computed
], AuthManager.prototype, "isUnderage", null);
__decorate([
    computed
], AuthManager.prototype, "avatarUrl", null);
__decorate([
    computed
], AuthManager.prototype, "orgLogoUrl", null);
__decorate([
    computed
], AuthManager.prototype, "orgName", null);
__decorate([
    computed
], AuthManager.prototype, "orgState", null);
__decorate([
    computed
], AuthManager.prototype, "isUserProfileEmpty", null);
__decorate([
    computed
], AuthManager.prototype, "isDataUser", null);
__decorate([
    computed
], AuthManager.prototype, "passwordUpdatedAt", null);
__decorate([
    computed
], AuthManager.prototype, "organizationIsLive", null);
__decorate([
    computed
], AuthManager.prototype, "isOrgAdmin", null);
__decorate([
    computed
], AuthManager.prototype, "isResearcher", null);
__decorate([
    computed
], AuthManager.prototype, "gfidProvider", null);
__decorate([
    computed
], AuthManager.prototype, "proxyAccess", null);
__decorate([
    computed
], AuthManager.prototype, "blockedFromUsingIAL2", null);
__decorate([
    computed
], AuthManager.prototype, "isIAL2Authorized", null);
__decorate([
    computed
], AuthManager.prototype, "isSandbox", null);
__decorate([
    computed
], AuthManager.prototype, "associatedPatientMRN", null);
__decorate([
    computed
], AuthManager.prototype, "consentingPatientInfo", null);
__decorate([
    observable
], AuthManager.prototype, "accountLinkingConflict", void 0);
